import { Injectable } from '@angular/core';

import { IControl } from 'app/services/api5-service/api.interface';
import { Observable, combineLatest, of } from 'rxjs';
import {
  distinctUntilChanged,
  map,
  mapTo,
  startWith,
  take,
} from 'rxjs/operators';

import { FormService } from './form.service';

import { IFormModel } from '../dynamic-form.interface';
import {
  ISPFieldConfig,
  ISPFieldType,
  ISPFieldTypeWithControl,
} from '../model';
import { filterConfigs } from '../utils/formly-configs';
import { getControlPlaceholder } from '../utils/get-control-placeholder';
import { isConfigWithControl } from '../utils/is-config-with-control';
import { removePropertyList } from '../utils/remove-prop';

/**
 * Service for controls mixed field states
 *
 * "Mixed" is a mechanism to distinct fields values while group edit process
 *
 * Obviously, multiple entities can have different value for some property. We can't properly display this multiple values.
 * And when user try to perform group edit he will see "That field have multiple values" label on field
 *
 * Field considered "mixed" until user set some certain value - it means he try to perform edition with this value for multiple entities
 * So, after some value setted, field are no longer considered as "mixed"
 *
 * BE need to know about which fields need to edit while group edition, and if user do not set any value for, then FE send NO values to BE
 * It means that BE should keep old values of entities
 */
@Injectable()
export class MixedService {
  constructor(private readonly formService: FormService) {}

  /**
   * Get all names of model fields, that considered as mixed
   */
  private getMixedControlNames(): string[] {
    return filterConfigs<ISPFieldConfig<ISPFieldTypeWithControl>>(
      this.formService.configs,
      isConfigWithControl,
    )
      .filter(config =>
        this.isControlMixed(config.props.originalControl, config.type),
      )
      .map(config => {
        if (config.type === ISPFieldType.Phone) {
          return [
            config.props.originalControl.$name,
            config.props.selectCountryProps.originalControl.$name,
          ];
        }
        return config.props.originalControl.$name;
      })
      .flat();
  }

  /**
   * Check that control considered as mixed
   *
   * @param control
   * @param type
   */
  isControlMixed(control: IControl, type: ISPFieldType): boolean {
    if (control.$mixed !== 'yes') {
      return false;
    }

    switch (type) {
      // phone field is hardly can be detected, because it's never is empty, because of mask
      // slider field cannot be empty by it's nature
      // checkbox field never is empty, it's always 'off' or 'on'
      case ISPFieldType.Phone:
      case ISPFieldType.Slider:
      case ISPFieldType.Checkbox: {
        const isValueWasChanged = Boolean(
          this.formService.formControl(control.$name)?.dirty,
        );
        return !isValueWasChanged;
      }
      default: {
        const isValueEmpty = this.formService.model[control.$name] === '';
        return isValueEmpty;
      }
    }
  }

  /**
   * Stream of control mixed state
   *
   * @param control
   * @param type
   */
  isControlMixed$(control: IControl, type: ISPFieldType): Observable<boolean> {
    if (control.$mixed !== 'yes') {
      return of(false);
    }

    switch (type) {
      case ISPFieldType.Slider:
      case ISPFieldType.Checkbox:
        // wait for checkbox changed, then it will be considered as not mixed
        // @WARN do not use 'pristine' because of validation error of BE errors
        return this.formService
          .formControlValueChange$(control.$name)
          .pipe(distinctUntilChanged(), take(1), mapTo(false), startWith(true));
      default:
        // by default field considered as 'mixed' if there is no value. If user set some value - then he want to edit mixed field, so it's not mixed anymore
        return this.formService
          .formControlValueChange$(control.$name, true)
          .pipe(
            map(value => {
              const isValueEmpty = value === '';
              return isValueEmpty;
            }),
          );
    }
  }

  getControlPlaceholder$(
    control: IControl,
    type: ISPFieldType,
  ): Observable<string> {
    return combineLatest([
      this.isControlMixed$(control, type),
      this.formService.doc$,
    ]).pipe(
      map(([isMixed, doc]) =>
        getControlPlaceholder(doc, {
          control,
          isMixed,
          type,
        }),
      ),
    );
  }

  getControlPlaceholder(control: IControl, type: ISPFieldType): string {
    return getControlPlaceholder(this.formService.loadedDoc, {
      control,
      isMixed: this.isControlMixed(control, type),
      type,
    });
  }

  /**
   * Get form model with disabled controls and without mixed controls
   */
  getUnmixedFormModel(): IFormModel {
    return removePropertyList(
      this.formService.formGroup.getRawValue(),
      this.getMixedControlNames(),
    );
  }
}
